import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CollectionGuide, TopicLinks, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { EmptyState } from '@todo/web-ui/components/empty-state';
import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourGrid } from '@todo/web-ui/components/tour-grid';
import { RelatedGuides, relatedGuidesSeed } from '@todo/web-ui/components/related-guides';
import {
  COLLECTION_DUPLICATES_SEED_KEY,
  collectionBySlug,
  collectionSeedKey,
  isCollectionIndexable,
  topicsOf,
  type Collection,
} from '@todo/web-ui/lib/collections';
import { formatPrice } from '@todo/web-ui/lib/format';
import { COLLECTIONS, SEE_ALSO_PAGES } from '@/lib/collections';
import { collectionQuery } from '@/lib/prefetch';
import { useTours } from '@todo/core/hooks/use-tours';
import { DEFAULT_LOCALE, isSupportedLocale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { LIST_BUCKET } from '@todo/core/list-queries';
import { breadcrumbJsonLd, faqPageJsonLd, seoTitle } from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';
import type { Tour } from '@todo/core/tours';

// One page per collection (src/lib/collections.ts): /desert-safari,
// /dhow-cruise, /date-night, /dubai-marina… — the Dubai counterpart of
// riviera's /wine-tours, on the same tours_by_theme RPC.
/** The tour with the lowest "from" price, as its card shows it; null when none has one. */
function lowestPriced(tours: Tour[]): Tour | null {
  let best: Tour | null = null;
  for (const tour of tours) {
    if (tour.from_price == null) continue;
    if (!best || Number(tour.from_price) < Number(best.from_price)) best = tour;
  }
  return best;
}

export function generateStaticParams({ params }: { params: { lang?: string } }): { lang: string; collection: string }[] {
  const lang = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  return COLLECTIONS.map((c) => ({ lang, collection: c.slug }));
}

export default function CollectionRoute() {
  const { collection: slug } = useLocalSearchParams<{ collection: string }>();
  const collection = collectionBySlug(slug);
  // A slug that isn't a collection: only reachable by client-side navigation
  // (only collections are exported).
  if (!collection) return <NotFoundScreen />;
  return <CollectionScreen collection={collection} />;
}

function CollectionScreen({ collection }: { collection: Collection }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const seedKey = collectionSeedKey(collection.slug);
  const { tours, loading } = useTours(collectionQuery(collection.theme), seedKey);
  const title = t(`collections.${collection.slug}.title`);
  const copy = useCollectionCopy(collection.slug);
  // The category collections carry an SEO <title> and a keyword <h1> of
  // their own; the others fall back to the short title.
  const pageTitle = t(`collections.${collection.slug}.seoTitle`, { defaultValue: title });
  const heading = t(`collections.${collection.slug}.heading`, { defaultValue: title });
  // A topic sits under its category in the breadcrumb; a category links to its topics.
  const parentCrumb = collection.parent
    ? [{ name: t(`collections.${collection.parent}.title`), path: collection.parent }]
    : [];
  const parent = collectionBySlug(collection.parent);
  const related = parent ? [parent] : topicsOf(collection.slug);
  const seeAlsoPages = SEE_ALSO_PAGES[collection.slug] ?? [];
  // The build's verdict, baked into the page so the client's head matches.
  const duplicateOf = getPrefetched<Record<string, string>>(LIST_BUCKET, COLLECTION_DUPLICATES_SEED_KEY)?.[collection.slug];
  // A page answering "… price" searches has a `metaFrom` with the lowest card
  // price, read from the same seed as the cards (so the client's head matches);
  // the copy itself stays free of prices.
  const cheapest = lowestPriced(tours);
  const metaFrom = cheapest
    ? t(`collections.${collection.slug}.metaFrom`, {
        price: formatPrice(Number(cheapest.from_price), cheapest.currency, locale),
        defaultValue: '',
      })
    : '';

  return (
    <Page>
      <PageHead
        title={seoTitle(pageTitle)}
        description={metaFrom || t(`collections.${collection.slug}.meta`)}
        path={collection.slug}
        indexable={isCollectionIndexable(copy.guide.length > 0, tours.length, duplicateOf)}>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [...parentCrumb, { name: title }]))}
        </script>
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
        {seedScript(seedKey)}
        {seedScript(COLLECTION_DUPLICATES_SEED_KEY)}
        {collection.guides?.length ? relatedGuidesSeed(collection.slug, locale) : null}
      </PageHead>

      <Section id="collection-title" level={1} title={heading}>
        <Intro>{t(`collections.${collection.slug}.intro`)}</Intro>
        {related.length || seeAlsoPages.length ? <TopicLinks collections={related} pages={seeAlsoPages} /> : null}
        {tours.length ? (
          <TourGrid tours={tours} />
        ) : loading ? (
          <CardGrid>
            <CardSkeletons />
          </CardGrid>
        ) : (
          <EmptyState exclude={collection.slug} />
        )}
      </Section>

      <CollectionGuide slug={collection.slug} copy={copy} />

      <RelatedGuides page={collection.slug} guides={collection.guides} />
    </Page>
  );
}
